<script setup lang="ts">
  import { useTranslation } from '@/composables/useTranslation'
  import {
    Download,
    FolderSymlink,
    MoreHorizontal,
    Pencil,
    Settings,
    Trash2
  } from 'lucide-vue-next'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { computed } from 'vue'

  const props = withDefaults(
    defineProps<{
      hasMessages: boolean
      isStreaming?: boolean
      settingsCount?: number
      /**
       * When ``true``, the settings count badge is suppressed while the menu is
       * shown in pre-chat state (no messages yet). The "Einstellungen" entry then
       * renders without a count next to it.
       *
       * Used by the side chat and fullscreen chat ``•••`` menus where the count
       * is considered noise before a conversation has started.
       */
      hidePreChatSettingsCount?: boolean
    }>(),
    { hidePreChatSettingsCount: false }
  )

  const showSettingsCount = computed(
    () => !!props.settingsCount && (!props.hidePreChatSettingsCount || props.hasMessages)
  )

  const emit = defineEmits<{
    'download-pdf': []
    rename: []
    organize: []
    delete: []
    settings: []
  }>()

  const { t } = useTranslation({
    en: {
      DownloadChatAsPdf: 'Download Chat as PDF',
      Rename: 'Rename',
      Organize: 'Organize',
      DeleteChat: 'Delete Chat',
      Settings: 'Settings'
    },
    de: {
      DownloadChatAsPdf: 'Chat als PDF herunterladen',
      Rename: 'Umbenennen',
      Organize: 'Organisieren',
      DeleteChat: 'Chat löschen',
      Settings: 'Einstellungen'
    }
  })
</script>

<template>
  <!--
    A kebab menu has no reason to behave as a true modal — there's no focus trap or
    background pointer-event blocking that benefits the user — so ``:modal="false"``
    routes radix-vue through ``MenuRootContentNonModal``, which skips both the focus
    trap and the body scroll-lock. The global ``ConfigProvider`` in ``App.vue`` already
    suppresses the body padding-right / white-margin side effect for the surfaces that
    do need to stay modal (Dialog, AlertDialog); this just avoids touching the page
    scroll behaviour at all for a transient dropdown.
  -->
  <DropdownMenu :modal="false">
    <DropdownMenuTrigger as-child>
      <button
        class="flex h-6 w-6 items-center justify-center text-gray-800 hover:opacity-70"
        data-cy="chat-options-menu-trigger"
        @click.stop
      >
        <MoreHorizontal
          class="h-6 w-6"
          :stroke-width="1.25"
        />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="end"
      :side-offset="8"
      class="flex min-h-[52px] w-[280px] flex-col justify-center overflow-hidden rounded-xl border-[#E5E7EB] px-0
        py-2.5 shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)]"
      data-cy="chat-options-menu-content"
    >
      <template v-if="hasMessages">
        <DropdownMenuItem
          class="gap-2 rounded-none border-b border-[#F9FAFB] p-4 text-sm text-[#1F2937]"
          data-cy="chat-options-download-pdf"
          @click="emit('download-pdf')"
        >
          <Download class="h-4 w-4 shrink-0" />
          {{ t('DownloadChatAsPdf') }}
        </DropdownMenuItem>
        <DropdownMenuItem
          class="gap-2 rounded-none border-b border-[#F9FAFB] p-4 text-sm text-[#1F2937]"
          data-cy="chat-options-rename"
          @click="emit('rename')"
        >
          <Pencil class="h-4 w-4 shrink-0" />
          {{ t('Rename') }}
        </DropdownMenuItem>
        <DropdownMenuItem
          class="gap-2 rounded-none border-b border-[#F9FAFB] p-4 text-sm text-[#1F2937]"
          data-cy="chat-options-organize"
          @click="emit('organize')"
        >
          <FolderSymlink class="h-4 w-4 shrink-0" />
          {{ t('Organize') }}
        </DropdownMenuItem>
        <DropdownMenuItem
          class="gap-2 rounded-none border-b border-[#F9FAFB] p-4 text-sm text-[#1F2937]"
          :disabled="isStreaming"
          data-cy="chat-options-delete"
          @click="emit('delete')"
        >
          <Trash2 class="h-4 w-4 shrink-0" />
          {{ t('DeleteChat') }}
        </DropdownMenuItem>
      </template>
      <DropdownMenuItem
        class="gap-2 rounded-none p-4 text-sm text-[#1F2937]"
        data-cy="chat-options-settings"
        @click="emit('settings')"
      >
        <Settings class="h-4 w-4 shrink-0" />
        <span class="flex-1">{{ t('Settings') }}</span>
        <span
          v-if="showSettingsCount"
          class="flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-200 px-1.5 text-xs font-medium
            text-gray-700"
          data-cy="chat-options-settings-count"
        >
          {{ settingsCount }}
        </span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
